import { request } from '../request';

// 用于在途请求去重 (In-flight request deduplication)，防止 React 严格模式或双重挂载引发重复网络请求
const inFlightRequests = new Map<string, Promise<any>>();

function getDeduplicatedRequest<T>(cacheKey: string, requestFn: () => Promise<T>): Promise<T> {
  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey)!;
  }

  const promise = requestFn().finally(() => {
    setTimeout(() => {
      inFlightRequests.delete(cacheKey);
    }, 300);
  });

  inFlightRequests.set(cacheKey, promise);
  return promise;
}

/**
 * 获取我的工单列表
 * GET /support-tickets
 *
 * @param params 筛选及分页参数
 * @returns 工单列表分页响应
 */
export function fetchSupportTickets(params?: Api.SupportTicket.GetTicketListParams) {
  const cacheKey = `support-tickets:list:${params?.page || 1}:${params?.page_size || 20}:${params?.status || ''}:${params?.ticket_type || ''}:${params?.priority || ''}:${params?.created_at_from || ''}:${params?.created_at_to || ''}:${params?.latest_feedback_at_from || ''}:${params?.latest_feedback_at_to || ''}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.SupportTicket.TicketListResponse>({
      url: '/support-tickets',
      method: 'get',
      params,
    })
  );
}

/**
 * 提交工单
 * POST /support-tickets
 *
 * @param data 工单创建数据
 * @returns 创建成功的工单对象
 */
export function createSupportTicket(data: Api.SupportTicket.CreateRequest) {
  return request<Api.SupportTicket.TicketResponse>({
    url: '/support-tickets',
    method: 'post',
    data,
  });
}

/**
 * 获取我的工单详情（含全部可见反馈）
 * GET /support-tickets/{id}
 *
 * @param id 工单 ID
 * @returns 工单详情响应
 */
export function fetchSupportTicketDetail(id: string | number) {
  return getDeduplicatedRequest(`support-tickets:detail:${id}`, () =>
    request<Api.SupportTicket.TicketResponse>({
      url: `/support-tickets/${id}`,
      method: 'get',
    })
  );
}

/**
 * 增量获取工单反馈
 * GET /support-tickets/{id}/feedback
 *
 * @param id 工单 ID
 * @param params 分页与游标参数
 * @returns 工单反馈列表增量响应
 */
export function fetchTicketFeedbacks(id: string | number, params?: Api.SupportTicket.GetFeedbackListParams) {
  const cacheKey = `support-tickets:feedback:${id}:${params?.after_id || ''}:${params?.before_id || ''}:${params?.page_size || 50}`;
  return getDeduplicatedRequest(cacheKey, () =>
    request<Api.SupportTicket.FeedbackListResponse>({
      url: `/support-tickets/${id}/feedback`,
      method: 'get',
      params,
    })
  );
}

/**
 * 追加工单反馈
 * POST /support-tickets/{id}/feedback
 *
 * @param id 工单 ID
 * @param data 反馈追加数据
 * @returns 新追加的工单反馈模型
 */
export function createTicketFeedback(id: string | number, data: Api.SupportTicket.FrontendFeedbackRequest) {
  return request<Api.SupportTicket.FeedbackResponse>({
    url: `/support-tickets/${id}/feedback`,
    method: 'post',
    data,
  });
}
